import { Box } from "@chakra-ui/react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { KIN_TOOL_PROGRESS_EXIT_MS } from "./agent-tool-progress";

export type KinAssistantTurnSlotKind =
  | "answer"
  | "progress"
  | "thinking"
  | "none";

export type KinAssistantTurnSlotProps = {
  kind: KinAssistantTurnSlotKind;
  answer: ReactNode;
  progress: ReactNode;
  thinking: ReactNode;
};

function nodeForKind(
  kind: KinAssistantTurnSlotKind,
  nodes: { answer: ReactNode; progress: ReactNode; thinking: ReactNode },
): ReactNode {
  if (kind === "answer") return nodes.answer;
  if (kind === "progress") return nodes.progress;
  if (kind === "thinking") return nodes.thinking;
  return null;
}

/**
 * Progress / thinking / answer share one grid cell and crossfade so the reply replaces
 * status in place instead of stacking underneath and jumping the layout.
 *
 * Multiple exiting layers can coexist (e.g. progress → thinking → answer within 150ms)
 * so a fast follow-up kind does not cancel the prior fade.
 */
export function KinAssistantTurnSlot({
  kind,
  answer,
  progress,
  thinking,
}: KinAssistantTurnSlotProps) {
  const cacheRef = useRef({ answer, progress, thinking });
  if (answer != null) cacheRef.current.answer = answer;
  if (progress != null) cacheRef.current.progress = progress;
  if (thinking != null) cacheRef.current.thinking = thinking;

  const [phase, setPhase] = useState<{
    current: KinAssistantTurnSlotKind;
    exiting: KinAssistantTurnSlotKind[];
  }>(() => ({ current: kind, exiting: [] }));
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const exitTimersRef = useRef<Map<KinAssistantTurnSlotKind, number>>(
    new Map(),
  );

  useEffect(() => {
    if (kind === phaseRef.current.current) return;

    const from = phaseRef.current.current;
    setPhase((prev) => {
      let exiting = prev.exiting.filter((slot) => slot !== kind);
      if (from !== "none" && from !== kind && !exiting.includes(from)) {
        exiting = [...exiting, from];
      }
      return { current: kind, exiting };
    });

    if (from === "none" || from === kind) return;

    const existingTimer = exitTimersRef.current.get(from);
    if (existingTimer != null) {
      window.clearTimeout(existingTimer);
    }

    const timeoutId = window.setTimeout(() => {
      exitTimersRef.current.delete(from);
      setPhase((prev) => ({
        ...prev,
        exiting: prev.exiting.filter((slot) => slot !== from),
      }));
    }, KIN_TOOL_PROGRESS_EXIT_MS);
    exitTimersRef.current.set(from, timeoutId);
  }, [kind]);

  useEffect(() => {
    return () => {
      for (const timeoutId of exitTimersRef.current.values()) {
        window.clearTimeout(timeoutId);
      }
      exitTimersRef.current.clear();
    };
  }, []);

  const liveNodes = {
    answer: answer ?? cacheRef.current.answer,
    progress: progress ?? cacheRef.current.progress,
    thinking: thinking ?? cacheRef.current.thinking,
  };

  const renderLayer = (
    slotKind: KinAssistantTurnSlotKind,
    exiting: boolean,
  ) => {
    const node = nodeForKind(slotKind, liveNodes);
    if (node == null) return null;
    return (
      <Box
        key={slotKind}
        gridArea="1 / 1"
        w="100%"
        opacity={exiting ? 0 : 1}
        transition={`opacity ${KIN_TOOL_PROGRESS_EXIT_MS}ms ease-out`}
        pointerEvents={exiting ? "none" : undefined}
        aria-hidden={exiting || undefined}
        data-exiting={exiting || undefined}
        css={{
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        {node}
      </Box>
    );
  };

  return (
    <Box
      display="grid"
      w="100%"
      data-testid="kin-assistant-turn-slot"
      data-slot={phase.current}
    >
      {phase.exiting.map((slotKind) => renderLayer(slotKind, true))}
      {phase.current !== "none" ? renderLayer(phase.current, false) : null}
    </Box>
  );
}

export function resolveKinAssistantTurnSlotKind(options: {
  hasText: boolean;
  hasProgress: boolean;
  thinkingDots: boolean;
}): KinAssistantTurnSlotKind {
  if (options.hasText) return "answer";
  if (options.hasProgress) return "progress";
  if (options.thinkingDots) return "thinking";
  return "none";
}
